export class SocialShare {
    /**
     * @param {object} config
     * @param {string} config.shareUrl     - URL to include in fallback shares
     * @param {string} config.shareGraphics - Relative path to the share image
     * @param {string} [config.appTitle]   - Title for native share sheet
     */
    constructor({ shareUrl, shareGraphics, appTitle = "Adidas Football Challenge" }) {
        this.shareUrl = shareUrl;
        this.shareGraphics = shareGraphics;
        this.appTitle = appTitle;
        this._fileCache = null;
    }

    // ─── Public ──────────────────────────────────────────────────────────────

    /**
     * @param {"twitter"|"whatsapp"|"instagram"|"link"} platform
     * @param {string} shareText  - The share message (with score already injected)
     */
    async share(platform, shareText) {
        switch (platform) {
            case "twitter":   return this._shareTwitter(shareText);
            case "whatsapp":  return this._shareWhatsApp(shareText);
            case "instagram": return this._shareInstagram(shareText);
            case "link":      return this._shareLink(shareText);
            default:
                console.warn("[SocialShare] Unknown platform:", platform);
        }
    }

    // ─── Platform handlers ────────────────────────────────────────────────────

    async _shareTwitter(shareText) {
        window.open(
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText + " " + this.shareUrl)}`,
            "_blank"
        );
    }

    async _shareWhatsApp(shareText) {
        const fullText = shareText + " " + this.shareUrl;
        if (navigator.share) {
            const done = await this._nativeShare(fullText);
            if (done) return;
        }
        window.open(
            `https://api.whatsapp.com/send?text=${encodeURIComponent(fullText)}`,
            "_blank"
        );
    }

    async _shareInstagram(shareText) {
        if (navigator.share) {
            const done = await this._nativeShare(shareText);
            if (done) return;
        }
        window.open("https://www.instagram.com", "_blank");
    }

    async _shareLink(shareText) {
        const fullText = shareText + " " + this.shareUrl;
        if (navigator.share) {
            const done = await this._nativeShare(fullText);
            if (done) return;
        }
        // iOS uses &body=, Android uses ?body=
        window.open(`sms:?&body=${encodeURIComponent(fullText)}`, "_self");
    }

    // ─── Web Share API ────────────────────────────────────────────────────────

    async _nativeShare(shareText) {
        const file = await this._getShareFile();

        // iOS does not allow files + url together — try files first, then url-only
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
                await navigator.share({ title: this.appTitle, text: shareText, files: [file] });
                return true;
            } catch (e) {
                if (e.name === "AbortError") return false;
                // files share failed, fall through to url-only below
            }
        }

        try {
            await navigator.share({ title: this.appTitle, text: shareText, url: this.shareUrl });
            return true;
        } catch (e) {
            if (e.name !== "AbortError") console.warn("[SocialShare] navigator.share failed:", e);
            return false;
        }
    }

    // ─── Image file helpers ───────────────────────────────────────────────────

    async _getShareFile() {
        if (this._fileCache) return this._fileCache;
        if (!this.shareGraphics) return null;
        try {
            const res = await fetch(this.shareGraphics);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const name = this.shareGraphics.split("/").pop();
            this._fileCache = new File([blob], name, { type: blob.type });
            return this._fileCache;
        } catch (e) {
            console.warn("[SocialShare] Could not load share image:", e);
            return null;
        }
    }
}
